import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { CaretDownIcon } from '@phosphor-icons/react';
import type { CustomerCollectionStatus } from '@erp/rpc';
import { cn } from '@/lib/utils';

type Variant = 'default' | 'secondary' | 'outline' | 'destructive';

const STATUS_STYLE: Record<
  CustomerCollectionStatus,
  { variant: Variant; className?: string }
> = {
  current: { variant: 'secondary' },
  promised_to_pay: {
    variant: 'outline',
    className: 'border-amber-400 text-amber-700',
  },
  disputed: {
    variant: 'outline',
    className: 'border-orange-400 text-orange-700',
  },
  in_collections: { variant: 'destructive' },
  do_not_contact: {
    variant: 'outline',
    className: 'text-muted-foreground',
  },
};

const STATUS_ORDER: CustomerCollectionStatus[] = [
  'current',
  'promised_to_pay',
  'disputed',
  'in_collections',
  'do_not_contact',
];

function useStatusLabels(): Record<CustomerCollectionStatus, string> {
  const gt = useGT();
  return {
    current: gt('Current'),
    promised_to_pay: gt('Promised'),
    disputed: gt('Disputed'),
    in_collections: gt('In Collections'),
    do_not_contact: gt('Do Not Contact'),
  };
}

interface CollectionStatusChipProps {
  status: CustomerCollectionStatus;
  onChange: (status: CustomerCollectionStatus) => void;
  disabled?: boolean;
  isPending?: boolean;
}

export function CollectionStatusChip({
  status,
  onChange,
  disabled,
  isPending,
}: CollectionStatusChipProps) {
  const gt = useGT();
  const labels = useStatusLabels();
  const [open, setOpen] = useState(false);
  const style = STATUS_STYLE[status];
  const label = labels[status];

  function handleSelect(next: string) {
    if (next !== status) onChange(next as CustomerCollectionStatus);
    setOpen(false);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild disabled={disabled || isPending}>
        <button
          type="button"
          className="inline-flex"
          onClick={(e) => e.stopPropagation()}
          aria-label={gt('Collection status: {label}', { label })}
        >
          <Badge
            variant={style.variant}
            className={cn(
              'cursor-pointer hover:opacity-80 gap-1',
              style.className,
              isPending && 'opacity-60',
            )}
          >
            {label}
            <CaretDownIcon className="h-3 w-3" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>
          <T>Collection status</T>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={status} onValueChange={handleSelect}>
          {STATUS_ORDER.map((s) => (
            <DropdownMenuRadioItem key={s} value={s}>
              {labels[s]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
